/*
 * Preserve campaign attribution from the landing page through the Shopify cart.
 * Cart attributes are carried into the order and give Shopify/analytics pixels a
 * first-party source of truth even after the browser URL no longer has the UTM.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'tp_utm_attribution';
  var COOKIE_KEY = 'tp_utm_attribution';
  var MAX_AGE = 60 * 60 * 24 * 30;
  var FIELDS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
    'utm_id',
    'gclid',
    'fbclid',
    'epik',
    'msclkid',
    'ttclid'
  ];
  var syncPromise = Promise.resolve();

  function cartUrl(path) {
    var root =
      window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';
    return root + path;
  }

  function readStorage() {
    try {
      var stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch (error) {
      // Fall through to the first-party cookie.
    }

    try {
      var match = document.cookie.match(
        new RegExp('(?:^|; )' + COOKIE_KEY.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
      );
      return match ? JSON.parse(decodeURIComponent(match[1])) : {};
    } catch (error) {
      return {};
    }
  }

  function writeStorage(data) {
    var serialized;

    try {
      serialized = JSON.stringify(data);
      window.localStorage.setItem(STORAGE_KEY, serialized);
    } catch (error) {
      serialized = serialized || JSON.stringify(data);
    }

    try {
      document.cookie =
        COOKIE_KEY +
        '=' +
        encodeURIComponent(serialized) +
        '; path=/; max-age=' +
        MAX_AGE +
        '; SameSite=Lax' +
        (window.location.protocol === 'https:' ? '; Secure' : '');
    } catch (error) {
      // The cart sync below can still use the in-memory values for this page.
    }
  }

  function captureAttribution() {
    var params = new URLSearchParams(window.location.search);
    var stored = readStorage();
    var captured = {};
    var hasIncomingAttribution = false;

    FIELDS.forEach(function (field) {
      var value = params.get(field);
      if (value) {
        captured[field] = value;
        hasIncomingAttribution = true;
      } else if (stored[field]) {
        captured[field] = stored[field];
      }
    });

    if (hasIncomingAttribution) {
      captured.captured_at = new Date().toISOString();
      writeStorage(captured);
    }

    return captured;
  }

  function cartAttributes(attribution) {
    var attributes = {};

    FIELDS.forEach(function (field) {
      if (attribution[field]) attributes['tp_' + field] = attribution[field];
    });

    if (attribution.captured_at) {
      attributes.tp_attribution_captured_at = attribution.captured_at;
    }

    return attributes;
  }

  function applyToProductForms(attribution) {
    var forms = document.querySelectorAll(
      'form[data-type="add-to-cart-form"], form[action*="/cart/add"]'
    );

    forms.forEach(function (form) {
      FIELDS.forEach(function (field) {
        if (!attribution[field]) return;

        var propertyName = '_tp_' + field;
        var inputName = 'properties[' + propertyName + ']';
        var existing = Array.from(form.elements).find(function (element) {
          return element.name === inputName;
        });

        if (!existing) {
          var input = document.createElement('input');
          input.type = 'hidden';
          input.name = inputName;
          input.value = attribution[field];
          form.appendChild(input);
        }
      });
    });
  }

  function syncCart(attribution) {
    var desired = cartAttributes(attribution);
    if (!Object.keys(desired).length) return Promise.resolve();

    syncPromise = syncPromise.then(function () {
      return fetch(cartUrl('cart.js'), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Unable to read cart for attribution sync.');
          return response.json();
        })
        .then(function (cart) {
          var existing = cart.attributes || {};
          var merged = Object.assign({}, existing);
          var changed = false;

          Object.keys(desired).forEach(function (key) {
            if (merged[key] !== desired[key]) {
              merged[key] = desired[key];
              changed = true;
            }
          });

          if (!changed) return null;

          return fetch(cartUrl('cart/update.js'), {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ attributes: merged })
          }).then(function (response) {
            if (!response.ok) throw new Error('Unable to persist attribution on cart.');
            return response.json();
          });
        });
    });

    return syncPromise.catch(function () {
      // Attribution must never block shopping or checkout if Shopify rejects the
      // background cart update.
    });
  }

  var attribution = captureAttribution();

  if (Object.keys(cartAttributes(attribution)).length) {
    applyToProductForms(attribution);
    syncCart(attribution);

    document.addEventListener('product-ajax:added', function () {
      syncCart(attribution);
    });

    document.addEventListener('cart:updated', function () {
      applyToProductForms(attribution);
      syncCart(attribution);
    });

    document.addEventListener('shopify:section:load', function () {
      applyToProductForms(attribution);
    });

    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        var submitter = event.submitter;
        var action = form && form.getAttribute && form.getAttribute('action');

        if (
          !form ||
          !action ||
          new URL(action, window.location.origin).pathname !== '/cart' ||
          !submitter ||
          submitter.name !== 'checkout' ||
          form.dataset.tpAttributionReady === 'true'
        ) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        syncCart(attribution).finally(function () {
          form.dataset.tpAttributionReady = 'true';
          if (form.requestSubmit) {
            form.requestSubmit(submitter);
          } else {
            form.submit();
          }
        });
      },
      true
    );
  }

  window.TaylorPentonAttribution = {
    get: function () {
      return Object.assign({}, attribution);
    },
    sync: function () {
      return syncCart(attribution);
    }
  };
})();
